import * as React from 'react';
import { Alert, Button, Col, Form, InputGroup, Row } from 'react-bootstrap';
import { HelpIcon, SearchIcon, SortIcon } from '../../components/ui/icons';
import { useDeviceSetup } from './useDeviceSetUp';
import { ColumnDef } from '@tanstack/react-table';
import TextInputField from '../../components/ui/textInputField';
import Heading from '../../components/ui/heading';
import TableView from '../../components/tableView';
import TreeView from '../../components/treeView';
import useHandleGroupSearch from '../deviceManagement/useHandleGroupSearch';
import { DataAvailabilityMessages, routes, Status } from '../../common/constants';
import './devicesetup.scss';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FLASHCAST_USER_GUIDE_URL } from '../../common/constants';
import { useNavigate } from 'react-router-dom';

interface IDeviceSetupColumns {
  displayName: string;
  deviceId: string;
  organizations: string;
  version: number;
  iotAgentVersion: string;
  firmwareVersion: string;
  groupName?: string;
}


const DeviceSetUp = () => {
  const { handleGroupInputChange, groupSearchQuery, nodesToDisplay } =
    useHandleGroupSearch({ isEditable: false });
    const navigate = useNavigate();

  const {
    deviceId,
    deviceCode,
    alertMessage,
    addDeviceResponse,
    handleDeviceIdInputChange,
    handleDataFromTreeView,
    handleDeviceCodeInputChange,
    handleCloseAlert,
    handleAddButtonClick,
    findGroupNameById,
    selectGroupState,
    setSelectGroupState,
    groupsStatus,
    refresh,
    setRefresh,
    addedDevices,
    deviceIdFlag,
    inputErrors
  } = useDeviceSetup();

  const columns: ColumnDef<IDeviceSetupColumns, string>[] = React.useMemo(
    () => [
      {
        accessorFn: (row) => `${row?.deviceId}`,
        accessorKey: 'deviceId',
        header: (row) => (
          <div className='table-heading'>
            Device ID{' '}
            <span>
              <SortIcon />
            </span>
          </div>
        ),
        cell: (info) => info.getValue(),
        enableColumnFilter: false,
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => `${row?.displayName}`,
        accessorKey: 'deviceName',
        header: (row) => (
          <div className='table-heading'>
            Device Name{' '}
            <span>
              <SortIcon />
            </span>
          </div>
        ),
        footer: (props) => props.column.id,
        enableColumnFilter: false,
        cell: (row) => <div className='table-cell'>{row.getValue()}</div>,
      },
      {
        accessorFn: (row) => `${row?.groupName}`,
        accessorKey: 'deviceGroup',
        header: (row) => (
          <div className='table-heading'>
            Groups{' '}
            <span>
              <SortIcon />
            </span>
          </div>
        ),
        footer: (props) => props.column.id,
        enableColumnFilter: false,
        cell: (row) => <div className='table-cell'>{row.getValue()}</div>,
      },
      {
        accessorFn: (row) => `${row?.iotAgentVersion}`,
        accessorKey: 'iotAgentVersion',
        header: (row) => (
          <div className='table-heading'>
            IoT Agent Version{' '}
            <span>
              <SortIcon />
            </span>
          </div>
        ),
        footer: (props) => props.column.id,
        enableColumnFilter: false,
        cell: (row) => <div className='table-cell'>{row.getValue()}</div>,
      },
      {
        accessorFn: (row) => `${row?.firmwareVersion}`,
        accessorKey: 'firmwareVersion',
        header: (row) => (
          <div className='table-heading'>
            Firmware Version{' '}
            <span>
              <SortIcon />
            </span>
          </div>
        ),
        footer: (props) => props.column.id,
        enableColumnFilter: false,
        cell: (row) => <div className='table-cell'>{row.getValue()}</div>,
      },
    ],
    []
  );

  return (
    <div className='device-setup-container'>
      <Col>
        <div className='device-setup-heading'>
          <h1>Device Set Up</h1>

          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="help-tooltip">View User Guide</Tooltip>}
          >
            <div 
              className="help-icon-wrapper"
              onClick={() => navigate(routes.userGuide)}
            >
              <HelpIcon />
            </div>
          </OverlayTrigger>
        </div>
      </Col>

      <Col>
        <div className='device-setup-content'>
          <Row>
            <Col sm={5}>
              <div className='device-setup-input'>
                <TextInputField
                  heading='Device ID'
                  placeholder='Enter Device ID'
                  ariaLabel='device-id'
                  value={deviceId}
                  onChange={handleDeviceIdInputChange}
                />
                {deviceIdFlag ? (
                <span className='device-setup-required'>
                  * Only uppercase letters and numbers are allowed.
                </span>
              ) : inputErrors.deviceId ? (
                <div className="input-error-msg">
                  {inputErrors.deviceId}
                </div>
    ) : null}
              </div>
            </Col>
            <Col sm={5}>
              <div className='choose-group'>
                <h2> Choose Group (optional)</h2>
                <div className='chooseGroupInput'>
                  <InputGroup>
                    <Form.Control
                      placeholder='Search'
                      aria-label='Search'
                      aria-describedby='search-tree-list'
                      value={groupSearchQuery}
                      onChange={handleGroupInputChange}
                    />
                    <InputGroup.Text id='search-tree-list'>
                      <SearchIcon />
                    </InputGroup.Text>
                  </InputGroup>
                </div>
                <div className='treenode-dropdown'>
                  {groupsStatus === Status.SUCCESS && (
                    <TreeView
                      nodes={nodesToDisplay}
                      onDataFromTreeView={handleDataFromTreeView}
                      isModalOrg={true}
                      isMultiSelect={true}
                      selectGroupState={selectGroupState}
                      setSelectGroupState={setSelectGroupState}
                      isSelectAllEnabled={true}
                      reload={refresh}
                      setReload={setRefresh}
                    ></TreeView>
                  )}
                </div>
              </div>
            </Col>
          </Row>
          <Row>
            <Col sm={5}>
              <div className='device-setup-input'>
                <TextInputField
                  heading='Device Code'
                  placeholder='Enter Device Code'
                  ariaLabel='device-code'
                  value={deviceCode}
                  onChange={handleDeviceCodeInputChange}
                />
                {inputErrors.deviceCode && (
                     <div className="input-error-msg">
                        {inputErrors.deviceCode}
                    </div>
                )}
              </div>
            </Col>
          </Row>
          <Row>
            <Col sm={10}>
              <div className='device-setup-btn-container'>
                <Button
                  className='button-style-1'
                  onClick={handleAddButtonClick}
                >
                  Add
                </Button>
              </div>
            </Col>
          </Row>
        </div>
      </Col>
      <Col sm={12} className='deviceSetup'>
        <div className='device-setup-table'>
          <TableView
            data={addDeviceResponse?.devices ? addedDevices : []}
            columns={columns}
            noDataMessage={DataAvailabilityMessages.NO_DEVICE_ADDED}
          />
        </div>
      </Col>
    </div>
  );
};

export default DeviceSetUp;
